// externals
import { useDispatch, useSelector } from 'react-redux';
import type { SelectInputOption } from '@interstate/components/SelectInput';

// libraries
import * as tradeInsSelectors from '@makemydeal/dr-dash-store/selectors/tradeIns';
import * as tradeInsActionCreators from '@makemydeal/dr-dash-store/actions/tradeInsActionCreators';
import * as tradeInComponentSelectors from '@makemydeal/dr-dash-store/selectors/tradeInComponentSelectors';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { useSingleTradeInFormProvider } from '../MultiTradeIn/TradeInFormProvider';

// components
import { SelectInput } from './TradeInSelectInput';

// constants
import { TRADE_IN_TRIM } from '@makemydeal/dr-dash-components';
import { MANAGER_VIEW_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';

import { useLayoutEffect, useMemo } from 'react';
import { TextInput } from '@interstate/components/TextInput';

interface TrimFieldProps {
    isManualFormOpen: boolean;
    disabled?: boolean;
}

const TrimsField = ({ isManualFormOpen, disabled }: TrimFieldProps) => {
    const dispatch = useDispatch();
    const { tradeInIndex, tradeInValidator, tradeInFormErrors } = useSingleTradeInFormProvider();
    const vehicleTrim = useSelector(tradeInsSelectors.getTradeTrim(tradeInIndex));
    const vehicleYear = useSelector(tradeInsSelectors.getTradeYear(tradeInIndex));
    const vehicleMakeId = useSelector(tradeInsSelectors.getTradeMakeId(tradeInIndex));
    const vehicleModelId = useSelector(tradeInsSelectors.getTradeModelId(tradeInIndex));
    const vehicleTrimId = useSelector(tradeInsSelectors.getTradeTrimId(tradeInIndex));
    const isPreserveCrmTradeVehicleInfoEnabled = useSelector(featureToggleSelectors.isPreserveCrmTradeVehicleInfoEnabled);
    const tradeInVehicleTrims = useSelector(tradeInsSelectors.getTradeInComponentTrims(tradeInIndex));
    const tradeInVehicleModels = useSelector(tradeInsSelectors.getTradeInComponentModels(tradeInIndex));
    const tradeInVehicleMakes = useSelector(tradeInsSelectors.getTradeInComponentMakes(tradeInIndex));
    const tradeInVehicleYears = useSelector(tradeInComponentSelectors.getTradeInComponentYears);
    const tradeInVehicle = useSelector(tradeInsSelectors.getTradeInComponentVehicle(tradeInIndex));
    const vehicleModel = useSelector(tradeInsSelectors.getTradeModel(tradeInIndex));
    const trimDecodedOptions = useSelector(tradeInsSelectors.getTradeTrimDecodedOptions(tradeInIndex));
    const vinHasBeenDecoded = useSelector(tradeInsSelectors.vinHasBeenDecoded(tradeInIndex));

    const isTrimInputDisabled = tradeInVehicleTrims?.options?.length === 0 || !vehicleModel;

    const optionsSource = useMemo(() => {
        const noDecodedOptions = trimDecodedOptions.length === 0 || !trimDecodedOptions.some((option) => option.name);
        const trimsFallback = noDecodedOptions && vinHasBeenDecoded;
        const decodedOptionsSource = trimsFallback || !vinHasBeenDecoded ? tradeInVehicleTrims?.options : trimDecodedOptions;

        if (!isPreserveCrmTradeVehicleInfoEnabled || !vinHasBeenDecoded) {
            return decodedOptionsSource;
        }

        const catalogTrims = tradeInVehicleTrims?.options ?? [];
        const catalogTrimNames = new Set(catalogTrims.map((trim) => trim.name?.toLowerCase()));
        const decodedOnlyTrims = trimDecodedOptions.filter((trim) => trim.name && !catalogTrimNames.has(trim.name.toLowerCase()));

        return [...catalogTrims, ...decodedOnlyTrims];
    }, [tradeInVehicleTrims?.options, trimDecodedOptions, vinHasBeenDecoded, isPreserveCrmTradeVehicleInfoEnabled]);

    // the trim saved on the deal can differ in casing from the lookup options, so prefer the option's own casing
    const normalizedTrimValue = useMemo(() => {
        if (!isPreserveCrmTradeVehicleInfoEnabled) return vehicleTrim;

        return optionsSource?.find((trim) => trim.name?.toLowerCase() === vehicleTrim.toLowerCase())?.name ?? vehicleTrim;
    }, [optionsSource, vehicleTrim, isPreserveCrmTradeVehicleInfoEnabled]);

    useLayoutEffect(() => {
        tradeInValidator.setValue('vehicle.trim', normalizedTrimValue || '', { validate: true, clearErrors: false });
    }, [normalizedTrimValue, tradeInValidator]);

    const tradeInTrims = useMemo((): SelectInputOption[] => {
        const options = optionsSource?.map((trim) => ({ label: trim.name, value: trim.name })) || [];

        if (isPreserveCrmTradeVehicleInfoEnabled) {
            const selectedTrimAlreadyExists = options.some((option) => option.value === normalizedTrimValue);

            if (!selectedTrimAlreadyExists && normalizedTrimValue) {
                options.push({ label: normalizedTrimValue, value: normalizedTrimValue });
            }

            return options.filter((trim) => trim.label && trim.value).sort();
        }

        const selectedTrim = { label: vehicleTrim, value: vehicleTrim };

        const selectedTrimAlreadyExists = options.some(
            (option) => option.label === selectedTrim.label && option.value === selectedTrim.value
        );

        if (!selectedTrimAlreadyExists && vehicleTrim && vehicleTrim.length !== 0 && vehicleTrimId && vehicleTrimId.length !== 0) {
            options.push(selectedTrim);
        }

        return options.filter((trim) => trim.label && trim.value).sort();
    }, [optionsSource, normalizedTrimValue, vehicleTrim, vehicleTrimId, isPreserveCrmTradeVehicleInfoEnabled]);

    const onTrimChange = (event: any) => {
        const trim = optionsSource.find((trim) => trim.name === event.target.value);
        if (!trim) return;
        updateTrimSelector(trim);
    };

    const onTrimTextInputChange = (event: InterstateOnChangeEvent<string>) => {
        const trim = { name: event.target.value, id: '' };
        updateTrimSelector(trim);
    };

    const updateTrimSelector = (trim: { name: string; id: string }) => {
        tradeInValidator.setValue('vehicle.trim', trim.name, { validate: true, clearErrors: false });
        tradeInValidator.commit(['vehicle.trim'], { validate: true });

        dispatch(tradeInsActionCreators.updateTradeInTrim(trim, tradeInIndex));
        dispatch(tradeInsActionCreators.updateVehicle({ ...tradeInVehicle, trim }, tradeInIndex));
        if (trim && !vinHasBeenDecoded) {
            dispatch(
                tradeInsActionCreators.fetchTradeInComponentVehicle(
                    MANAGER_VIEW_APP_PREFIX,
                    vehicleYear,
                    vehicleMakeId,
                    vehicleModelId,
                    trim.id,
                    tradeInIndex
                )
            );
        }
    };

    const isLoading =
        tradeInVehicleTrims?.isLoading ||
        tradeInVehicleModels?.isLoading ||
        tradeInVehicleMakes?.isLoading ||
        tradeInVehicleYears?.isLoading;

    const noTrimOptions =
        tradeInVehicleTrims?.options?.length === 0 || !tradeInVehicleTrims?.options?.some((option) => option.name);

    if (noTrimOptions && !isLoading && vehicleModel) {
        return (
            <TextInput
                data-testid={`trade-trim-decoded-input`}
                label={TRADE_IN_TRIM}
                name={TRADE_IN_TRIM}
                onChange={onTrimTextInputChange}
                value={normalizedTrimValue}
                disabled={(isTrimInputDisabled && !vinHasBeenDecoded) || disabled}
                size="sm"
                hasError={Boolean(tradeInFormErrors?.vehicle?.trim?.hasError)}
                errorMessage={tradeInFormErrors?.vehicle?.trim?.errorMessage as string}
            ></TextInput>
        );
    }

    return (
        <>
            {isManualFormOpen && (
                <SelectInput
                    data-testid={`${vinHasBeenDecoded ? `trade-trim-decoded-select-input` : `trim-input`}`}
                    id={vinHasBeenDecoded ? `trade-trim-decoded-select-input` : undefined}
                    label={TRADE_IN_TRIM}
                    name={TRADE_IN_TRIM}
                    onChange={onTrimChange}
                    options={tradeInTrims}
                    value={normalizedTrimValue}
                    placeholder="Select"
                    disabled={(isTrimInputDisabled && !vinHasBeenDecoded) || disabled}
                    displayDeselectOption={false}
                    size="sm"
                    hasError={Boolean(tradeInFormErrors?.vehicle?.trim?.hasError)}
                    errorMessage={tradeInFormErrors?.vehicle?.trim?.errorMessage as string}
                />
            )}
        </>
    );
};

export default TrimsField;
